import { Component, computed, effect, EventEmitter, inject, input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BadgeModule } from 'primeng/badge';
import { FileUploadModule } from 'primeng/fileupload';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';

import { StorageObjectDto } from '@api/backend-connector/models';
import { SmartInputStringComponent } from 'src/app/components-form/smart-input-string/smart-input-string.component';
import { ButtonDirective } from 'src/app/directives/button.directive';
import { ImagePreviewComponent } from 'src/app/image-preview/image-preview.component';
import { FileSizeFormatPipe } from 'src/app/pipes/file-size-format.pipe';
import { AttachmentsService } from 'src/app/services/attachments.service';

type AttachmentItem =
  | {
    state: 'uploading';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    file: any;
  }
  | {
    state: 'uploaded';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    file: any;
    so: StorageObjectDto;
  }
  | {
    state: 'existent';
    so: StorageObjectDto;
  };

@Component({
  selector: 'app-attachment-item',
  standalone: true,
  imports: [
    BadgeModule,
    ButtonDirective,
    FileUploadModule,
    FileSizeFormatPipe,
    ImagePreviewComponent,
    ProgressBarModule,
    TooltipModule,
    TranslateModule,
    SmartInputStringComponent,
  ],
  templateUrl: './attachment-item.component.html',
  styleUrl: './attachment-item.component.scss',
})
export class AttachmentItemComponent {
  readonly attachmentsService = inject(AttachmentsService);

  readonly attachmentItem = input.required<AttachmentItem>();
  readonly isImage = input.required<boolean | undefined>();
  readonly uploading = input.required<boolean | undefined>();
  readonly canEdit = input.required<boolean>();
  readonly nameEditable = input.required<boolean>();
  @Output() upload = new EventEmitter<void>();
  @Output() removeUploading = new EventEmitter<MouseEvent>();
  @Output() removeUploaded = new EventEmitter<void>();
  @Output() removeExistent = new EventEmitter<void>();
  @Output() nameChanged = new EventEmitter<string>();

  readonly initialName = computed(() => {
    const item = this.attachmentItem();
    const name = item.state === 'uploading' ? item.file.name : item.so.name;
    return name;
  });

  readonly nameControl = computed(() => {
    return new FormControl<string>(this.initialName(), { nonNullable: true });
  });

  constructor() {
    effect(() => {
      const sub = this.nameControl().valueChanges.subscribe((value) => {
        this.nameChanged.emit(value);
      });
      return () => sub.unsubscribe();
    });
  }

  async download() {
    const item = this.attachmentItem();

    const getFile = async () => {
      // Case: File is still uploading - download local File object
      if (item.state === 'uploading' || item.state === 'uploaded') {
        return { blob: item.file, name: item.file.name };
      }
      // Case: File is uploaded - download from backend
      const fileId = item.so.id;
      try {
        return {
          blob: await firstValueFrom(this.attachmentsService.downloadFile({ id: fileId })),
          name: item.so.name,
        };
      } catch (err) {
        // TODO toast error
        console.error('Download failed:', err);
      }
      return;
    };

    const file = await getFile();
    if (file) {
      const url = URL.createObjectURL(file.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    }
  }
}
